import { LOWER_IS_BETTER } from '../constants'
import type {
  DeltaDirection,
  DeltaTone,
  ProgressComparison,
  ProgressPeriod,
  StatDelta,
} from '../types'
import { formatPeriodLabel } from './periodLabel'
import { periodValue } from './progressChart'
import { formatStatValue } from './statValues'

/** 'Aug 22 – Sep 20' — the window a delta is measured against. */
export const formatComparisonRange = (period: ProgressPeriod): string =>
  `${formatPeriodLabel(period.period_start, 'day')} – ${formatPeriodLabel(
    period.period_end,
    'day'
  )}`

const toTone = (direction: DeltaDirection, abbr: string): DeltaTone => {
  const isRise = direction === 'up'
  return isRise !== LOWER_IS_BETTER.includes(abbr) ? 'positive' : 'negative'
}

/** '+1' / '-1', formatted like the stat. */
const formatSignedAmount = (diff: number, abbr: string): string =>
  `${diff > 0 ? '+' : '-'}${formatStatValue(Math.abs(diff), abbr)}`

/**
 * '50% (+1)' — the percentage of the previous value with the raw change. A
 * previous 0 counts as 1 (from 0 a true percentage is infinite), so 0 → 2
 * reads '200% (+2)'.
 */
const formatChange = (current: number, previous: number, abbr: string) => {
  const diff = current - previous
  const base = previous === 0 ? 1 : Math.abs(previous)
  const percent = Math.round((Math.abs(diff) / base) * 100)
  return `${percent}% (${formatSignedAmount(diff, abbr)})`
}

const buildDelta = (
  current: number | null,
  previous: number | null,
  abbr: string,
  previousPeriod: ProgressPeriod
): StatDelta | null => {
  // No value in a window, or nothing changed: no pill.
  if (current === null || previous === null || current === previous) {
    return null
  }
  const direction: DeltaDirection = current > previous ? 'up' : 'down'
  return {
    direction,
    tone: toTone(direction, abbr),
    change: formatChange(current, previous, abbr),
    previousValue: formatStatValue(previous, abbr),
    previousRange: formatComparisonRange(previousPeriod),
  }
}

/**
 * A stat's range total against the previous window, read like a chart period
 * (`periodValue`: counts default to 0, a rate without games has no value).
 */
export const buildStatDelta = (
  comparison: ProgressComparison | null | undefined,
  abbr: string
): StatDelta | null =>
  comparison?.previous
    ? buildDelta(
        periodValue(comparison.current, abbr),
        periodValue(comparison.previous, abbr),
        abbr,
        comparison.previous
      )
    : null

/** Games played in the range against the previous window. */
export const buildGamesDelta = (
  comparison: ProgressComparison | null | undefined
): StatDelta | null =>
  comparison?.previous
    ? buildDelta(
        comparison.current.games_played,
        comparison.previous.games_played,
        'GP',
        comparison.previous
      )
    : null

/** 'vs 2 · Aug 22 – Sep 20' — the previous window under a delta. */
export const formatDeltaPrevious = (delta: StatDelta): string =>
  `vs ${delta.previousValue} · ${delta.previousRange}`

const DIRECTION_WORDS: Record<DeltaDirection, string> = {
  up: 'Up',
  down: 'Down',
}

/** Screen-reader text, e.g. 'Up 50% (+1) vs 2 · Aug 22 – Sep 20'. */
export const formatDeltaLabel = (delta: StatDelta): string =>
  `${DIRECTION_WORDS[delta.direction]} ${delta.change} ${formatDeltaPrevious(
    delta
  )}`
